import { createAction } from '@reduxjs/toolkit'
import { Cacheables } from 'cacheables'
import {
  ChatCompletionAssistantMessageParam,
  ChatCompletionUserMessageParam,
} from 'openai/resources'
import {
  Message as WukongMessage,
  PullMode,
} from 'wukongimjssdk'

import { AIChatLanguage } from '@/constants/ai-chat-language'
import { AIChatPolicies } from '@/constants/ai-chat-policy'
import { SECOND } from '@/constants/unit'
import {
  ContextMessageInfo,
  Message,
} from '@/model/message'
import { createAppAsyncThunk } from '@/redux/utils'
import { rejectUndefinedAndNull } from '@/utils/array'
import { axios, requestBaeApi } from '@/utils/axios'
import { waitForSDK } from '@/utils/bae-im-sdk'
import { base64Encode } from '@/utils/base64'

const cache = new Cacheables()

export const messageNamespace = 'message'

export interface ReadAllMessagesParams {
  creatorId: number
  creatorUserId: number
  fanUserId: number
  limit?: number
  order?: 'desc' | 'asc'
  messageSeq?: number
}

export const convertMessage = (message: WukongMessage): Message | undefined => {
  try {
    return {
      ...JSON.parse(JSON.stringify(message)),
      content: JSON.parse(message.content.text),
      timestamp: message.timestamp * SECOND,
      id: message.messageID,
    }
  } catch (error) {
    console.log(message, error)
    return undefined
  }
}

export interface SendMessageParams {
  content: string
  creatorId: number
  creatorUserId: number
  fanUserId: number
  latestMessage: Message | undefined
}

interface SendMessageResponse {
  data: {
    message_id: string
  }
}

export const sendMessage = createAppAsyncThunk(
  `${messageNamespace}/sendMessage`,
  async ({
    content,
    creatorId,
    creatorUserId,
    fanUserId,
    latestMessage,
  }: SendMessageParams) => {
    const result = await requestBaeApi<SendMessageResponse>({
      creatorId,
      url: '/co/v1/send_message',
      method: 'post',
      data: {
        uid: creatorUserId,
        follower_id: fanUserId,
        message: {
          header: {
            no_persist: 0,
            red_dot: 1,
            sync_once: 0,
          },
          from_uid: creatorUserId.toString(),
          channel_id: fanUserId.toString(),
          channel_type: 1,
          stream_no: '',
          subscribers: [],
          payload: base64Encode(JSON.stringify({
            type: 1,
            content,
          })),
        },
      },
    })

    void axios.post('/fanChat/msgEmployee/relation/add', {
      messageId: result.data.data.message_id,
      creatorId,
      fanUserId,
      content,
      createdAt: new Date().toISOString(),
      latestFanMessage: latestMessage?.fromUID === fanUserId.toString() ? {
        id: latestMessage.id,
        createdAt: new Date(latestMessage.timestamp).toISOString(),
      } : undefined,
    })
  },
)

export const readAllMessages = createAppAsyncThunk(
  `${messageNamespace}/readAllMessages`,
  async ({
    limit = 20,
    order = 'desc',
    creatorUserId,
    fanUserId,
    messageSeq,
  }: ReadAllMessagesParams) => {
    const end = messageSeq || 0
    const sdk = await waitForSDK({ userId: creatorUserId })
    if (sdk) {
      const messages = await sdk.getMessages(fanUserId.toString(), {
        startMessageSeq: (end - limit) < 0 ? 0 : (end - limit),
        endMessageSeq: end,
        limit: limit,
        pullMode: order === 'desc' ? PullMode.Up : PullMode.Down,
      })
      return {
        messages: messages.map(convertMessage).filter(rejectUndefinedAndNull),
        hasMore: messages.length === limit,
      }
    }

    return { messages: [], hasMore: false }
  },
)

export const upsertMessage = createAction<{
  creatorId: number,
  message: Message
}>(`${messageNamespace}/upsertMessage`)

export interface AIChatChoice {
  message: {
    role: string
    content: string | undefined
  }
  tone: AIChatPolicies
}

export interface ChatCompletionResponse {
  choices: AIChatChoice[],
  policyAction: AIChatPolicies
}

type BaseContextMessage = ChatCompletionUserMessageParam | ChatCompletionAssistantMessageParam
type ContextMessage = BaseContextMessage & Partial<ContextMessageInfo>

interface RequestChatCompletionParams {
  creatorId: number
  creatorUserId: number
  fanUserId: number
  language?: AIChatLanguage
  contextMessages?: ContextMessage[]
  forceRefresh?: boolean
}

export const requestChatCompletions = createAppAsyncThunk(
  `${messageNamespace}/requestChatCompletions`,
  async (
    {
      creatorId,
      creatorUserId,
      fanUserId,
      language,
      contextMessages,
      forceRefresh = false,
    }: RequestChatCompletionParams) => {
    const handler = async () => {
      let finalContextMessages = contextMessages
      const sdk = await waitForSDK({ userId: creatorUserId })
      if (finalContextMessages === undefined && sdk) {
        const messages = await sdk.getMessages(fanUserId.toString(), {
          startMessageSeq: 0,
          endMessageSeq: 0,
          limit: 10,
          pullMode: 1,
        })

        finalContextMessages = messages.reverse().map((message) => {
          return {
            role: String(message.channel.channelID) === String(fanUserId) ? 'user' : 'assistant',
            content: message.content.entity.text,
          }
        })
      }

      const { data: { policyAction, choices } } = await axios.post<ChatCompletionResponse>(
        '/message/completions',
        {
          fanUserId,
          creatorId,
          messages: finalContextMessages,
          model: 'creator-ai-v0.1.1',
          n: 5,
          temperature: 1,
          language,
        },
      )

      return {
        policyAction,
        choices: choices.filter(({ message: { content } }) => (content !== undefined && content !== '')),
      }
    }

    const cacheKey = `requestChatCompletions:${creatorId}:${fanUserId}:${language}`
    return forceRefresh ? handler() : cache.cacheable(handler, cacheKey, {
      cachePolicy: 'max-age',
      maxAge: 60 * SECOND,
    })
  },
)
